(() => {
  'use strict';

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const announce = (message) => {
    const liveRegion = document.getElementById('LiveRegion');
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 50);
  };

  const closeDetails = (details, returnFocus = false) => {
    if (!details?.open) return;
    details.open = false;
    if (returnFocus) details.querySelector('summary')?.focus();
  };

  const bindMobileNavigation = (root = document) => {
    root.querySelectorAll('[data-mobile-nav]').forEach((navigation) => {
      if (navigation.dataset.bound === 'true') return;
      navigation.dataset.bound = 'true';

      const panel = navigation.querySelector('.mobile-nav__panel');
      const summary = navigation.querySelector('summary');
      summary?.setAttribute('aria-expanded', String(navigation.open));

      navigation.addEventListener('toggle', () => {
        document.body.classList.toggle('menu-open', navigation.open);
        summary?.setAttribute('aria-expanded', String(navigation.open));
        if (navigation.open) {
          window.setTimeout(() => panel?.querySelector(focusableSelector)?.focus(), 20);
        }
      });

      navigation.querySelectorAll('[data-mobile-nav-close]').forEach((control) => {
        control.addEventListener('click', () => closeDetails(navigation, true));
      });

      panel?.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => closeDetails(navigation));
      });

      navigation.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDetails(navigation, true);
          return;
        }

        if (event.key !== 'Tab' || !navigation.open || !panel) return;
        const focusable = Array.from(panel.querySelectorAll(focusableSelector)).filter(
          (element) => element.offsetParent !== null
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  };

  const bindDropdowns = (root = document) => {
    root.querySelectorAll('.nav-dropdown').forEach((dropdown) => {
      if (dropdown.dataset.bound === 'true') return;
      dropdown.dataset.bound = 'true';
      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        document.querySelectorAll('.nav-dropdown[open]').forEach((other) => {
          if (other !== dropdown) other.open = false;
        });
      });
    });
  };

  const updateCartCount = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = cart.item_count;
      counter.classList.toggle('is-hidden', cart.item_count === 0);
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      link.setAttribute('aria-label', `Cart (${cart.item_count})`);
    });
  };

  const bindQuickAdd = (root = document) => {
    root.querySelectorAll('.quick-add-form').forEach((form) => {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const label = button?.querySelector('span') || button;
        if (!button || !label || button.disabled) return;

        const originalLabel = label.textContent;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form)
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(result.description || 'This item could not be added.');
          label.textContent = 'Added!';
          announce(`${result.product_title || 'Item'} added to cart.`);
          await updateCartCount();
        } catch (error) {
          label.textContent = 'Try again';
          announce(error.message || 'This item could not be added.');
        } finally {
          window.setTimeout(() => {
            label.textContent = originalLabel;
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }, 1800);
        }
      });
    });
  };

  const initialize = (root = document) => {
    bindMobileNavigation(root);
    bindDropdowns(root);
    bindQuickAdd(root);
  };

  initialize();

  window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
    if (!event.matches) return;
    document.querySelectorAll('[data-mobile-nav][open]').forEach((navigation) => closeDetails(navigation));
    document.body.classList.remove('menu-open');
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.nav-dropdown')) {
      document.querySelectorAll('.nav-dropdown[open]').forEach((dropdown) => {
        dropdown.open = false;
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.nav-dropdown[open]').forEach((dropdown) => closeDetails(dropdown, true));
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
